import { Link } from 'expo-router';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { usePublishedChangeLogEntries } from '@/src/features/changelog/use-changelog';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';

function categoryLabel(category: string) {
  return category
    .split('_')
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');
}

function ChangeLogDetails({ details }: { details: string }) {
  const lines = details.split(/\r?\n/);
  const hasDiffLines = lines.some(
    (line) => line.startsWith('+') || line.startsWith('-'),
  );

  if (!hasDiffLines) {
    return <Text style={styles.entryDetails}>{details}</Text>;
  }

  return (
    <View style={styles.detailsGroup}>
      {lines.map((line, index) => {
        if (!line.trim()) {
          return <View key={`spacer-${index}`} style={styles.diffSpacer} />;
        }

        const added = line.startsWith('+');
        const removed = line.startsWith('-');

        if (!added && !removed) {
          return (
            <Text key={`text-${index}`} style={styles.entryDetails}>
              {line}
            </Text>
          );
        }

        return (
          <View
            key={`diff-${index}`}
            style={[
              styles.diffLine,
              added ? styles.diffLineAdded : styles.diffLineRemoved,
            ]}
          >
            <Text
              style={[
                styles.diffLineText,
                added ? styles.diffLineTextAdded : styles.diffLineTextRemoved,
              ]}
            >
              {line}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

export default function ChangeLogScreen() {
  const changelog = usePublishedChangeLogEntries(50);

  return (
    <PublicPageShell
      title="Public Changelog"
      subtitle="A plain-language record of meaningful Frienzzi changes."
    >
      <View style={styles.introCard}>
        <Text style={styles.introTitle}>
          Transparency is part of the product.
        </Text>
        <Text style={styles.introText}>
          Frienzzi publishes changes that affect user functionality, privacy,
          safety, notifications, email behavior, ranking, feeds,
          recommendations, and algorithms.
        </Text>
      </View>
      {changelog.loading ? (
        <View style={styles.loading}>
          <ActivityIndicator color={colors.primary} />
          <Text style={styles.muted}>Loading changelog...</Text>
        </View>
      ) : changelog.error ? (
        <ErrorView message={changelog.error.message} />
      ) : changelog.entries.length === 0 ? (
        <View style={styles.empty}>
          <Text style={styles.emptyTitle}>No published changes yet</Text>
          <Text style={styles.muted}>
            Public updates will appear here as Frienzzi changes.
          </Text>
        </View>
      ) : (
        <View style={styles.timeline}>
          {changelog.entries.map((entry, index) => (
            <View key={entry.id} style={styles.timelineRow}>
              <View style={styles.timelineMarkerColumn}>
                <View style={styles.timelineDot} />
                {index < changelog.entries.length - 1 ? (
                  <View style={styles.timelineLine} />
                ) : null}
              </View>
              <View style={styles.entryCard}>
                <Text style={styles.entryMeta}>
                  {formatDate(entry.publishedAt)} ·{' '}
                  {categoryLabel(entry.category)} ·{' '}
                  {entry.impact === 'major' ? 'Major' : 'Minor'}
                </Text>
                <Text style={styles.entryTitle}>{entry.title}</Text>
                <Text style={styles.entrySummary}>{entry.summary}</Text>
                {entry.details ? (
                  <ChangeLogDetails details={entry.details} />
                ) : null}
              </View>
            </View>
          ))}
        </View>
      )}
      <Link href="/mission" asChild>
        <Button title="Read the mission statement" variant="secondary" />
      </Link>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  introCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  introTitle: {
    color: colors.text,
    fontSize: 19,
    fontWeight: '900',
  },
  introText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  loading: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.xl,
  },
  muted: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  empty: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.xl,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  timeline: {
    gap: 0,
  },
  timelineRow: {
    flexDirection: 'row',
  },
  timelineMarkerColumn: {
    alignItems: 'center',
    width: 28,
  },
  timelineDot: {
    backgroundColor: colors.primary,
    borderColor: colors.surface,
    borderRadius: 7,
    borderWidth: 2,
    height: 14,
    marginTop: spacing.lg,
    width: 14,
  },
  timelineLine: {
    backgroundColor: colors.border,
    flex: 1,
    width: 2,
  },
  entryCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    gap: spacing.sm,
    marginBottom: spacing.lg,
    padding: spacing.lg,
  },
  entryMeta: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  entryTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  entrySummary: {
    color: colors.text,
    fontSize: 16,
    lineHeight: 23,
  },
  entryDetails: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  detailsGroup: {
    gap: spacing.xs,
  },
  diffLine: {
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  diffLineAdded: {
    backgroundColor: '#e8f6ee',
  },
  diffLineRemoved: {
    backgroundColor: '#fdebed',
  },
  diffLineText: {
    fontFamily: 'Courier',
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
  },
  diffLineTextAdded: {
    color: '#167944',
  },
  diffLineTextRemoved: {
    color: '#9f1d2d',
  },
  diffSpacer: {
    height: spacing.xs,
  },
});
